import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import {select, Store} from '@ngrx/store';
import {
  ClientDelete,
  ClientsList,
  getClientError,
  getClientSuccess, getPackagesListAll, getPaginatedClients,
  IClientState, IPackageState, IOrganizationState, PackagesListAll,
  OrganizationsListAll, getOrganizationsListAll,
  ResetClientState,
  IAuthenticationState,
  getLoggedInUser
} from '@app/stores';
import {Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {takeUntil} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {Client, Package, Organization} from '@app/modules/shared/models';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { Clipboard } from '@angular/cdk/clipboard';
import moment from 'moment-timezone';

import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { EntityLinkDirective } from '../../../_components/unified/entity-form-modal/entity-link.directive';
import { CreatableSelectComponent } from '../../../_components/creatable-select/creatable-select.component';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { SbTableComponent } from '@app/_components/unified/sb-table/sb-table.component';
import { SbTableActionsDirective, SbTableCellDirective, SbTableFilterExtraDirective } from '@app/_components/unified/sb-table/sb-table.directives';
import { SbTableColumn, SbTableOptions, SbTableServerState } from '@app/_components/unified/sb-table/sb-table.types';
@Component({
  selector: 'app-client-list',
  templateUrl: './client-list.component.html',
  styleUrls: ['./client-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, EntityLinkDirective, RouterLink, CreatableSelectComponent, SbTableComponent, SbTableCellDirective,
    SbTableActionsDirective, SbTableFilterExtraDirective, MatMenuTrigger, MatMenu]
})
export class ClientListComponent implements OnInit, OnDestroy {
  private clientStore = inject<Store<IClientState>>(Store);
  private toastr = inject(ToastrService);
  private _modalService = inject(NgbModal);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private clipboardService = inject(Clipboard);
  private packageStore = inject<Store<IPackageState>>(Store);
  private organizationStore = inject<Store<IOrganizationState>>(Store);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);


  unsubscriber = new Subject();
  readonly clients = signal<Client[]>([]);
  readonly totalClients = signal(0);
  readonly token = signal<any>(undefined);
  link: string;
  readonly packages = signal<Package[]>([]);
  readonly organizations = signal<Organization[]>([]);
  selectedPackage = 'All';
  selectedOrganization = 'All';
  readonly currentUser = signal<any>(undefined);

  // Ids are the names the list sorts by (orderBy.column); the address keeps the column index, as before.
  readonly tableColumns: SbTableColumn[] = [
    {id: 'logo', header: 'Logo', sortable: false, width: '130px'},
    {id: 'name', header: 'Name', width: '200px'},
    {id: 'email', header: 'Email', sortable: false, width: '200px'},
    {id: 'organization', header: 'Organization', sortable: false, width: '150px'},
    {id: 'package', header: 'Package', sortable: false, width: '150px'},
    {id: 'package_start_date', header: 'Package Start Date', sortable: false, width: '180px'},
    {id: 'package_end_date', header: 'Package End Date', sortable: false, width: '180px'},
    {id: 'created_at', header: 'Created date'},
    {id: 'projects', header: 'Projects', sortable: false},
  ];
  readonly tableOptions: SbTableOptions = {
    serverSide: true,
    urlState: true,
    urlSortByIndex: true,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
    defaultSort: {columnId: 'name', direction: 'asc'},
    selectable: false,
  };

  constructor() {
    this.clientStore.dispatch(ResetClientState({params: {error: '', paginatedClients: null, success: ''}}));
    this.subscribeStore();
  }

  subscribeStore() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.currentUser.set(user);
        }
      });
    this.clientStore.pipe(select(getClientError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
    this.clientStore.pipe(select(getClientSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          this.toastr.success(success, 'Success');
          this.loadAllClients();
        }
      });
    this.clientStore.pipe(select(getPaginatedClients))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(paginatedClients => {
        if (paginatedClients) {
          this.clients.set(paginatedClients.list);
          this.totalClients.set(paginatedClients.paging.total);
          this.token.set(paginatedClients.trial_registration_url);
        }
      });
    this.packageStore.pipe(select(getPackagesListAll))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.packages.set(data);
        }
      });
    this.organizationStore.pipe(select(getOrganizationsListAll))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.organizations.set(data);
        }
      });
  }

  ngOnInit(): void {
    this.packageStore.dispatch(PackagesListAll({}));
    this.organizationStore.dispatch(OrganizationsListAll({}));
  }

  /** The table asks for a page: the request the list has always sent for it. */
  onTableState(state: SbTableServerState) {
    const orderBy = state.sort ? {column: state.sort.columnId, dir: state.sort.direction} : undefined;
    this.loadAllClients(state.page - 1, state.pageLength, state.search, orderBy);
  }

  loadAllClients(page = 0, perPage = 10, search = '', orderBy= {column: 'name', dir: 'asc'}) {
    this.clientStore.dispatch(ClientsList({
      params: {
        options: JSON.stringify({includePackage: true, includeOrganization: true, includePagination: true, orderBy}),
        ...(this.selectedPackage !== 'All' ? {package_id: this.selectedPackage} : {}),
        ...(this.selectedOrganization !== 'All' ? {organization_id: this.selectedOrganization} : {}),
        page: page + 1,
        perPage,
        search
      }
    }));
  }

  onClickProjects(client) {
    // const projectListFilter = JSON.parse(localStorage.getItem('projectFilterList')) ? JSON.parse(localStorage.getItem('projectFilterList')) : {};
    // projectListFilter['selectedClient'] = client.id;
    // projectListFilter['selectedClientName'] = client.name;
    // localStorage.setItem('projectFilterList', JSON.stringify(projectListFilter));
    this.router.navigate(['/projects'],{queryParams: {clientId: client.id}});
  }

  formatMoment(insertTime) {
    insertTime = moment(insertTime, 'YYYY-MM-DD[T]HH[:]mm[:]00[.000Z]');
    if (insertTime.isValid()) {
      return insertTime.format('DD-MM-YYYY hh:mm a');
    }
    return null;
  }

  formatMomentDate(insertTime) {
    insertTime = moment(insertTime, 'YYYY-MM-DD');
    if (insertTime.isValid()) {
      return insertTime.format('DD-MM-YYYY');
    }
    return null;
  }

  trialRegister() {
    this.link = `${environment.frontUrl.replace('#', '')}register/${this.token()}`;
    return this.link;
  }

  copyLinkToClipboard(): void {
    this.clipboardService.copy(this.link);
    this.toastr.success('Link is Copied');
  }

  clientListByPackage(event = null) {
    this.selectedPackage = 'All';
    if (event) {
      this.selectedPackage = event.value;
    }
    this.loadAllClients();
  }

  clientListByOrganization(event = null) {
    this.selectedOrganization = 'All';
    if (event) {
      this.selectedOrganization = event.value;
    }
    this.loadAllClients();
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

  deleteClient(client: Client) {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.clientStore.dispatch(ClientDelete({clientId: client.id}));
      }
    });
  }

}
